import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Search, Filter, Users, Building, FileText, Calendar } from 'lucide-react';

const NotificationScopeModal = ({ 
  isOpen, 
  onClose, 
  eventType, 
  studies, 
  sites, 
  onSave 
}) => {
  const [scopeConfig, setScopeConfig] = useState({
    scope_type: 'global', // global, study, site, participant, dcq, assessment
    study_ids: [],
    site_ids: [],
    participant_ids: [],
    dcq_ids: [],
    assessment_ids: [],
    schedule_ids: []
  });

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Reset scope config when modal opens
      setScopeConfig({
        scope_type: 'global',
        study_ids: [],
        site_ids: [],
        participant_ids: [],
        dcq_ids: [],
        assessment_ids: [],
        schedule_ids: []
      });
    }
  }, [isOpen]);

  if (!isOpen || !eventType) return null;

  const scopeOptions = [
    {
      key: 'global',
      label: 'All Events',
      description: 'Receive notifications for all instances of this event type',
      icon: '🌍'
    },
    {
      key: 'study',
      label: 'Specific Studies',
      description: 'Only get notifications from selected studies',
      icon: '🔬'
    },
    {
      key: 'site',
      label: 'Specific Sites',
      description: 'Only get notifications from selected sites',
      icon: '🏥'
    },
    {
      key: 'participant',
      label: 'Specific Participants',
      description: 'Only get notifications about selected participants',
      icon: '👤'
    },
    {
      key: 'dcq',
      label: 'Specific DCQ Types',
      description: 'Only get notifications for certain data query types',
      icon: '📝'
    },
    {
      key: 'assessment',
      label: 'Specific Assessments',
      description: 'Only get notifications for certain assessment types',
      icon: '📋'
    }
  ];

  const getAvailableScopes = () => {
    // Only show relevant scoping options based on event type
    const eventCategory = eventType.key.split('.')[0];
    
    const relevantScopes = ['global']; // Always show global
    
    if (eventCategory === 'survey' || eventCategory === 'participant') {
      relevantScopes.push('study', 'site', 'participant');
    } else if (eventCategory === 'dcq') {
      relevantScopes.push('study', 'site', 'dcq');
    } else if (eventCategory === 'assessment') {
      relevantScopes.push('study', 'site', 'assessment');
    } else if (eventCategory === 'study') {
      relevantScopes.push('study');
    } else if (eventCategory === 'site') {
      relevantScopes.push('study', 'site');
    }
    
    return scopeOptions.filter(option => relevantScopes.includes(option.key));
  };

  const handleScopeChange = (scopeType) => {
    setScopeConfig(prev => ({
      ...prev,
      scope_type: scopeType,
      // Clear other selections when changing scope type
      study_ids: scopeType === 'study' ? prev.study_ids : [],
      site_ids: scopeType === 'site' ? prev.site_ids : [],
      participant_ids: scopeType === 'participant' ? prev.participant_ids : [],
      dcq_ids: scopeType === 'dcq' ? prev.dcq_ids : [],
      assessment_ids: scopeType === 'assessment' ? prev.assessment_ids : [],
      schedule_ids: scopeType === 'schedule' ? prev.schedule_ids : []
    }));
  };

  const handleEntitySelection = (entityType, entityId, isSelected) => {
    setScopeConfig(prev => ({
      ...prev,
      [`${entityType}_ids`]: isSelected
        ? [...prev[`${entityType}_ids`], entityId]
        : prev[`${entityType}_ids`].filter(id => id !== entityId)
    }));
  };

  const handleSave = () => {
    onSave(scopeConfig);
    onClose();
  };

  const getEntityList = () => {
    const { scope_type } = scopeConfig;
    
    if (scope_type === 'study') {
      return studies.filter(study => 
        study.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        study.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else if (scope_type === 'site') {
      return sites.filter(site =>
        site.site_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.site_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return [];
  };

  const filteredEntities = getEntityList();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70" />
      
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Advanced Notification Settings
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Configure when to receive "{eventType.key}" notifications
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Scope Selection */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Notification Scope</h4>
            
            <div className="grid gap-3">
              {getAvailableScopes().map((option) => (
                <label
                  key={option.key}
                  className={`flex items-start p-4 border rounded-lg cursor-pointer transition-all ${
                    scopeConfig.scope_type === option.key
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="scope_type"
                    value={option.key}
                    checked={scopeConfig.scope_type === option.key}
                    onChange={() => handleScopeChange(option.key)}
                    className="mt-1 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      <span className="text-lg mr-2">{option.icon}</span>
                      <span className="font-medium text-gray-900">{option.label}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Entity Selection */}
            {scopeConfig.scope_type !== 'global' && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Select {scopeConfig.scope_type === 'study' ? 'Studies' : 
                           scopeConfig.scope_type === 'site' ? 'Sites' : 
                           scopeConfig.scope_type === 'participant' ? 'Participants' : 
                           'Items'}
                </h4>
                
                {/* Search */}
                {(scopeConfig.scope_type === 'study' || scopeConfig.scope_type === 'site') && (
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}

                {/* Entity List */}
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  {filteredEntities.length > 0 ? (
                    filteredEntities.map((entity) => {
                      const isSelected = scopeConfig[`${scopeConfig.scope_type}_ids`].includes(entity.id);
                      
                      return (
                        <label
                          key={entity.id}
                          className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleEntitySelection(
                              scopeConfig.scope_type, 
                              entity.id, 
                              e.target.checked
                            )}
                            className="text-primary-600 focus:ring-primary-500"
                          />
                          <div className="ml-3">
                            <div className="font-medium text-gray-900">
                              {entity.title || entity.site_name || entity.name}
                            </div>
                            {entity.code && (
                              <div className="text-sm text-gray-500">
                                Code: {entity.code || entity.site_code}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })
                  ) : (
                    <div className="p-6 text-center text-gray-500">
                      {scopeConfig.scope_type === 'participant' ? (
                        <div>
                          <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p>Participant selection will be available after enabling basic notifications</p>
                        </div>
                      ) : scopeConfig.scope_type === 'dcq' ? (
                        <div>
                          <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p>DCQ type selection will be available after enabling basic notifications</p>
                        </div>
                      ) : (
                        <p>No {scopeConfig.scope_type}s found</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {scopeConfig.scope_type === 'global' 
                ? 'You will receive notifications for all events of this type'
                : `You will only receive notifications for selected ${scopeConfig.scope_type}s`
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NotificationScopeModal;
